import { supabase } from "@/shared/integrations/supabase/client";
import { getAuditSite } from "@/features/audit/api";
import { normalizeAuditSite } from "@/features/audit/normalizeAudit";
import { isNotFound } from "@/features/audit/useAuditDeficiencies";
import {
  parseJson,
  type DeficiencyPayload,
} from "@/features/properties/components/propertyOverview/deficiencyHelpers";
import type { Json } from "@/shared/integrations/supabase/types";
import { runWithConcurrency } from "./helpers";
import { resolveSurveyAttributes } from "@/features/site-survey-editor/services/resolveSurveyAttributes";
import { selectWithSurveySource } from "@/shared/integrations/supabase/surveySource";
import {
  PAGE_SIZE,
  PROPERTY_BATCH,
  type AssetNoteRow,
  type AssetRow,
  type AttributeRow,
  type SiteSummaryDocumentRow,
} from "./types";


// asset_notes was dropped from prod schema (June 2026 trim).

const AUDIT_CONCURRENCY = 8;

/**
 * Which of the given properties have ≥1 deficiency. Audit engine first; for
 * properties with no audit findings yet (404 / 403 / empty) during rollout,
 * fall back to the legacy property_deficiencies_and_recommendations blob.
 */
export async function fetchPropertyIdsWithDeficiencies(
  orgId: string,
  propertyIds: string[],
): Promise<Set<string>> {
  const out = new Set<string>();
  const needsLegacy: string[] = [];
  await runWithConcurrency(
    propertyIds.map((propertyId) => async () => {
      try {
        const audit = normalizeAuditSite(await getAuditSite(orgId, propertyId));
        if (audit.deficiencies.length > 0) out.add(propertyId);
        else needsLegacy.push(propertyId);
      } catch (err) {
        if (isNotFound(err)) needsLegacy.push(propertyId);
        // Other errors → treat as no deficiencies.
      }
    }),
    AUDIT_CONCURRENCY,
  );

  for (const propertyId of await fetchLegacyDeficiencyPropertyIds(needsLegacy)) {
    out.add(propertyId);
  }
  return out;
}

/** Properties with ≥1 deficiency in the legacy blob, chunked. */
async function fetchLegacyDeficiencyPropertyIds(propertyIds: string[]): Promise<string[]> {
  const found: string[] = [];
  for (let i = 0; i < propertyIds.length; i += PROPERTY_BATCH) {
    const chunk = propertyIds.slice(i, i + PROPERTY_BATCH);
    if (chunk.length === 0) break;
    const { data, error } = await supabase
      .from("property_deficiencies_and_recommendations")
      .select("property_id, data")
      .in("property_id", chunk);
    if (error) throw error;
    for (const row of (data ?? []) as Array<{ property_id: string; data: Json }>) {
      const payload = parseJson<DeficiencyPayload>(row.data, {});
      if (Array.isArray(payload.deficiencies) && payload.deficiencies.length > 0) {
        found.push(row.property_id);
      }
    }
  }
  return found;
}

export async function fetchLatestSiteSummaryDocuments(propertyIds: string[]): Promise<Map<string, SiteSummaryDocumentRow>> {
  const latest = new Map<string, SiteSummaryDocumentRow>();
  if (propertyIds.length === 0) return latest;

  for (let i = 0; i < propertyIds.length; i += PROPERTY_BATCH) {
    const chunk = propertyIds.slice(i, i + PROPERTY_BATCH);
    const { data, error } = await supabase
      .from("documents")
      .select("property_id, document_name, file_path, created_at")
      .eq("document_type", "site_summary")
      .in("property_id", chunk)
      .order("created_at", { ascending: false });

    if (error) throw error;

    for (const document of (data ?? []) as SiteSummaryDocumentRow[]) {
      if (document.property_id && !latest.has(document.property_id)) {
        latest.set(document.property_id, document);
      }
    }
  }

  return latest;
}

export async function fetchAssets(propertyIds: string[]): Promise<AssetRow[]> {
  const out: AssetRow[] = [];

  for (let i = 0; i < propertyIds.length; i += PROPERTY_BATCH) {
    const chunk = propertyIds.slice(i, i + PROPERTY_BATCH);
    let offset = 0;

    while (true) {
      const { data, error } = await supabase
        .from("property_assets")
        .select(
          "id, property_id, name, description, asset_categories(name), manufacturer, " +
          "model_name, model_number, serial_number, manufacture_date:manufacture_date_string, " +
          "estimated_age_years, condition_status, category_attributes, notes, project_zones(name), " +
          "nameplate_photo_url, asset_photo_url",
        )
        .in("property_id", chunk)
        .range(offset, offset + PAGE_SIZE - 1);

      if (error) throw error;
      if (!data || data.length === 0) break;

      out.push(...(data as unknown as AssetRow[]));
      if (data.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
    }
  }

  return out;
}

export async function fetchLatestAttributes(propertyIds: string[]): Promise<AttributeRow[]> {
  type Raw = { property_id: string; data: unknown; prod_data?: unknown; survey_source?: string | null; captured_at: string };
  const latest = new Map<string, Raw>();

  // Include survey_source only when the column exists (migration-safe).
  const attrCols = await selectWithSurveySource("property_id, data, prod_data, captured_at");
  for (let i = 0; i < propertyIds.length; i += PROPERTY_BATCH) {
    const chunk = propertyIds.slice(i, i + PROPERTY_BATCH);
    const { data, error } = await supabase
      .from("property_attributes")
      .select(attrCols)
      .in("property_id", chunk)
      .order("captured_at", { ascending: false });

    if (error) throw error;

    for (const row of (data ?? []) as unknown as Raw[]) {
      if (!latest.has(row.property_id)) latest.set(row.property_id, row);
    }
  }

  // Resolve each through the gate (self-describing prod_data when flagged, else data).
  const out: AttributeRow[] = [];
  for (const [propId, row] of latest) {
    // Guard the legacy `data` parse — a single malformed JSON string must not
    // abort the whole export. Fall back to an empty tree (prod_data-backed rows
    // render from prod_data regardless).
    let dataTree: unknown = [];
    try {
      dataTree = typeof row.data === "string" ? JSON.parse(row.data) : (row.data ?? []);
    } catch {
      dataTree = [];
    }
    const { fields } = resolveSurveyAttributes({
      surveySource: row.survey_source,
      prodData: (row.prod_data as Record<string, unknown> | null) ?? null,
      data: Array.isArray(dataTree) ? dataTree : [],
    });
    out.push({ property_id: propId, data: fields, captured_at: row.captured_at } as AttributeRow);
  }
  return out;
}

// asset_notes was dropped from prod schema (June 2026 trim). Return empty so
// the export bundle produces no notes rows rather than erroring.
export async function fetchAssetNotes(_assetIds: string[]): Promise<AssetNoteRow[]> {
  return [];
}
